import { BrandFamilyLine } from "@/components/brand-family";
import publicFaq from "@/features/public-pricing/faq.json";
import { PricingPlans } from "@/features/public-pricing/plans";
import { pricingCatalogOptions } from "@/features/public-pricing/query";
import { BrandLogo } from "@/components/brand-logo";
import { ThemeToggle } from "@/components/motion/theme-toggle";
import { useState, useEffect, type ReactNode } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import {
  ArrowRight,
  ArrowUpRight,
  Send,
  Inbox,
  Webhook,
  Check,
  Eye,
  MousePointerClick,
  ShieldCheck,
  ChevronRight,
} from "lucide-react";

import { NumberTicker } from "@/components/motion/number-ticker";
import { AnimatedBadge } from "@/components/motion/animated-badge";
import { BouncyAccordion } from "@/components/motion/bouncy-accordion";
import { ButtonLink } from "@/components/motion/button";
import { ButtonRouterLink } from "@/components/motion/button/router-link";
import { CopyButton } from "@/components/motion/copy-button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/motion/tabs";
import { LoginCta } from "@/components/landing/login-cta";
import { MaintenanceBanner } from "@/components/landing/maintenance-banner";
import { JsonLd } from "@/components/seo/json-ld";
import {
  landingAnalyticsSnippet,
  landingDomainSnippet,
  landingSendSnippet,
  landingWebhookSnippet,
} from "@/lib/landing-content";
import {
  breadcrumbSchema,
  faqSchema,
  homeOrganizationSchema,
  seoHead,
  softwareApplicationSchema,
  websiteSchema,
} from "@/lib/seo";

export const Route = createFileRoute("/")({
  ssr: true,
  loader: ({ context }) => context.queryClient.ensureQueryData(pricingCatalogOptions()),
  head: () =>
    seoHead({
      title: "One email API for humans, apps, AI agents — Bavimail",
      description:
        "One email API for humans, apps, and AI agents. Transactional and marketing sends, inbound webhooks, per-agent inboxes, and an MCP server for Claude, Cursor, and Cline. Compare current plans and included usage on our pricing page.",
      path: "/",
      keywords: [
        "email api for ai agents",
        "ai agent inbox",
        "transactional email api",
        "inbound email api",
        "email webhook platform",
        "resend alternative",
        "postmark alternative",
      ],
    }),
  component: LandingPage,
});

/* ─── Standard landing badge: one AnimatedBadge style for every tag ── */
const LANDING_BADGE = { shape: "pill", variant: "outline", size: "md" } as const;

/* Hero feature tag (sits inside a link; goes accent while hovered) */
function HeroTag({ children }: { children: ReactNode }) {
  const [hovered, setHovered] = useState(false);
  return (
    <AnimatedBadge
      {...LANDING_BADGE}
      status={hovered ? "accent" : "neutral"}
      contentKey="label"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {children}
    </AnimatedBadge>
  );
}

/* ─── Stacked title effect (from #3) ────────────────────── */
function StackedTitle({ text, className = "" }: { text: string; className?: string }) {
  return (
    <div className={`relative select-none ${className}`} style={{ paddingTop: "28px" }}>
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          className="absolute w-full font-hb font-extrabold uppercase leading-none tracking-tighter"
          style={{
            top: `${(3 - i) * 6}px`,
            fontSize: "inherit",
            color: "transparent",
            WebkitTextStroke: `1px rgba(255,255,255,${0.04 + i * 0.025})`,
          }}
        >
          {text}
        </div>
      ))}
      <div
        className="relative font-hb font-extrabold uppercase leading-none tracking-tighter text-white"
        style={{ fontSize: "inherit" }}
      >
        {text}
      </div>
    </div>
  );
}

/* ─── DashedBox with corner dots (from both) ────────────── */
function DashedBox({
  children,
  className = "",
  noPad = false,
}: {
  children: ReactNode;
  className?: string;
  noPad?: boolean;
}) {
  return (
    <div
      className={`relative border border-dashed border-[#2a2a2e] ${noPad ? "" : "p-5 md:p-6"} ${className}`}
    >
      <div className="absolute -top-[3px] -left-[3px] w-[6px] h-[6px] border border-[#444] bg-[#0d0d0f]" />
      <div className="absolute -top-[3px] -right-[3px] w-[6px] h-[6px] border border-[#444] bg-[#0d0d0f]" />
      <div className="absolute -bottom-[3px] -left-[3px] w-[6px] h-[6px] border border-[#444] bg-[#0d0d0f]" />
      <div className="absolute -bottom-[3px] -right-[3px] w-[6px] h-[6px] border border-[#444] bg-[#0d0d0f]" />
      {children}
    </div>
  );
}

/* ─── FAQ data ───────────────────────────────────────────── */
const faqItems = publicFaq.home.map((item) => ({ q: item.question, a: item.answer }));

// Same source as the FAQ JSON-LD below; ids are unique across both columns.
const faqAccordionItems = faqItems.map((faq, i) => ({
  id: `faq-${i}`,
  title: faq.q,
  description: faq.a,
}));

const homeStructuredData = [
  homeOrganizationSchema(),
  websiteSchema(),
  softwareApplicationSchema({
    name: "Bavimail",
    path: "/",
    description:
      "One email API for humans, apps, and AI agents. Transactional and marketing sends, inbound webhooks, per-agent inboxes, and an MCP server for Claude, Cursor, and Cline. Compare current plans and included usage on our pricing page.",
  }),
  faqSchema(
    faqItems.map((item) => ({
      question: item.q,
      answer: item.a,
    })),
  ),
  breadcrumbSchema([{ name: "Home", path: "/" }]),
];

/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */

function LandingPage() {
  const [scrollPct, setScrollPct] = useState(0);
  const [openFaq, setOpenFaq] = useState<string | null>(null);

  useEffect(() => {
    const onScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const docHeight =
        document.documentElement.scrollHeight - document.documentElement.clientHeight;
      setScrollPct(docHeight > 0 ? Math.min(scrollTop / docHeight, 1) : 0);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* ── Features with visuals (from #3 styling) ─────────── */
  const features = [
    {
      num: "01",
      icon: <Send className="w-4 h-4" />,
      title: "Outbound Processing",
      desc: "Deliver to any email client. Batch send campaigns, schedule timezone-aware delivery, and manage reputation at scale.",
      visual: (
        <div className="font-mono-hb text-[12px] space-y-2">
          {[
            { label: "batch.queued", detail: "2,400 recipients", color: "#f6821f" },
            { label: "schedule.set", detail: "TZ-aware · 9am local", color: "#3b82f6" },
            { label: "batch.sending", detail: "1,200 / 2,400", color: "#f6821f" },
            { label: "batch.delivered", detail: "99.8% · 0.18s avg", color: "#10b981" },
          ].map((evt) => (
            <div key={evt.label} className="flex items-center gap-2">
              <span
                className="w-1.5 h-1.5 rounded-full shrink-0"
                style={{ backgroundColor: evt.color }}
              />
              <span className="text-[#929292]">{evt.label}</span>
              <span className="ml-auto text-[#969696] text-[11px]">{evt.detail}</span>
            </div>
          ))}
        </div>
      ),
    },
    {
      num: "02",
      icon: <Inbox className="w-4 h-4" />,
      title: "Inbound Processing",
      desc: "MX record management, email parsing, and intelligent routing to webhooks or mailboxes.",
      visual: (
        <div className="font-mono-hb text-[12px] space-y-2">
          {["email.received", "email.parsed", "email.routed", "webhook.fired"].map((evt, j) => (
            <div key={evt} className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[#f6821f]" />
              <span className="text-[#929292]">{evt}</span>
              <span className="ml-auto text-[#969696]">{[8, 12, 6, 14][j]}ms</span>
            </div>
          ))}
        </div>
      ),
    },
    {
      num: "03",
      icon: <Eye className="w-4 h-4" />,
      title: "Open Tracking",
      desc: "Pixel-precise open tracking reveals the exact moment recipients engage.",
      visual: (
        <div>
          <div className="font-mono-hb text-[10px] text-[#969696] mb-2">Open rate / 7 days</div>
          <div className="flex items-end gap-1 h-16">
            {[35, 42, 38, 55, 48, 62, 58].map((h, j) => (
              <div
                key={j}
                className="flex-1 rounded-t-sm bg-[#f6821f]"
                style={{ height: `${h}%`, opacity: 0.3 + j * 0.1 }}
              />
            ))}
          </div>
          <div className="flex justify-between mt-1.5 font-mono-hb text-[9px] text-[#969696]">
            <span>Mon</span>
            <span>Tue</span>
            <span>Wed</span>
            <span>Thu</span>
            <span>Fri</span>
            <span>Sat</span>
            <span>Sun</span>
          </div>
        </div>
      ),
    },
    {
      num: "04",
      icon: <MousePointerClick className="w-4 h-4" />,
      title: "Click Analytics",
      desc: "Every link click captured and reported. Understand which content drives action.",
      visual: (
        <div className="font-mono-hb text-[12px] space-y-2.5">
          {[
            { url: "/pricing", clicks: 342, pct: 48 },
            { url: "/login", clicks: 218, pct: 31 },
            { url: "/docs", clicks: 156, pct: 22 },
          ].map((link) => (
            <div key={link.url}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[#f6821f]">{link.url}</span>
                <span className="text-[#8a8a8a]">
                  {link.clicks} <span className="text-[#969696]">clicks</span>
                </span>
              </div>
              <div className="w-full h-1.5 bg-[#1a1a1e] rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#f6821f]/40 rounded-full"
                  style={{ width: `${link.pct}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      ),
    },
    {
      num: "05",
      icon: <Webhook className="w-4 h-4" />,
      title: "Event Webhooks",
      desc: "Real-time HTTP callbacks for delivered, opened, clicked, bounced, and complained.",
      visual: (
        <div className="font-mono-hb text-[12px]">
          {["delivered", "opened", "clicked", "bounced"].map((evt, j) => (
            <div
              key={evt}
              className={`flex items-center gap-3 py-2 ${j > 0 ? "border-t border-dashed border-[#1a1a1e]" : ""}`}
            >
              <span
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: j === 3 ? "#ef4444" : "#f6821f" }}
              />
              <span className="text-[#929292]">email.{evt}</span>
              <ArrowRight className="w-3 h-3 text-[#969696] ml-auto" />
              <span className="text-[#969696]">POST</span>
            </div>
          ))}
        </div>
      ),
    },
    {
      num: "06",
      icon: <ShieldCheck className="w-4 h-4" />,
      title: "Reputation Management",
      desc: "Automatic domain health checks, suppression lists, bounce handling, and deliverability monitoring.",
      visual: (
        <div className="space-y-2.5">
          {/* Domain score bar */}
          <div className="flex items-center justify-between mb-1">
            <span className="font-mono-hb text-[10px] text-[#969696]">Domain health</span>
            <span className="font-mono-hb text-[11px] text-[#f6821f] font-bold">98.2</span>
          </div>
          <div className="w-full h-1.5 bg-[#1a1a1e] rounded-full overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-[#ef4444] to-[#f6821f]"
              style={{ width: "98%" }}
            />
          </div>
          {/* Checks */}
          {[
            { label: "SPF / DKIM / DMARC", status: "pass", color: "#f6821f" },
            { label: "Suppression sync", status: "active", color: "#f6821f" },
            { label: "Bounce rate", status: "0.12%", color: "#f6821f" },
            { label: "Blocklist scan", status: "clear", color: "#f6821f" },
          ].map((row) => (
            <div
              key={row.label}
              className="flex items-center justify-between font-mono-hb text-[11px]"
            >
              <span className="text-[#8a8a8a]">{row.label}</span>
              <span className="flex items-center gap-1.5" style={{ color: row.color }}>
                <span className="w-1 h-1 rounded-full" style={{ backgroundColor: row.color }} />
                {row.status}
              </span>
            </div>
          ))}
        </div>
      ),
    },
  ];

  /* ── Code examples (from #3) ─────────────────────────── */
  const examples = [
    {
      label: "Send an email",
      code: landingSendSnippet,
    },
    {
      label: "Handle webhooks",
      code: landingWebhookSnippet,
    },
    {
      label: "Domain setup",
      code: landingDomainSnippet,
    },
    {
      label: "Track engagement",
      code: landingAnalyticsSnippet,
    },
  ];

  const [activeExample, setActiveExample] = useState(examples[0].label);

  // explorePages is defined at module scope; see below the component.

  return (
    <>
      <JsonLd data={homeStructuredData} />
      <style>{`
        .font-hb { font-family: 'Manrope', system-ui, sans-serif; }
        .font-mono-hb { font-family: 'Chivo Mono', monospace; }

        .bg-grid-hb {
          background-image:
            linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px);
          background-size: 40px 40px;
        }

        .crosshatch-hb {
          background-image: repeating-linear-gradient(
            45deg, transparent, transparent 4px,
            rgba(255,255,255,0.015) 4px, rgba(255,255,255,0.015) 5px
          );
        }

        .crosshatch-dense-hb {
          background-image: repeating-linear-gradient(
            -45deg, transparent, transparent 3px,
            rgba(255,255,255,0.025) 3px, rgba(255,255,255,0.025) 4px
          );
        }

        @keyframes hb-dash-flow {
          from { stroke-dashoffset: 0; }
          to { stroke-dashoffset: -16; }
        }
        .hb-flow-line {
          stroke-dasharray: 6 4;
          animation: hb-dash-flow 1.5s linear infinite;
        }

        .hb-accent-bar {
          background: linear-gradient(90deg, #f6821f, #ff6633, #f6821f);
          background-size: 200% 100%;
        }

        @keyframes hb-dot-pulse {
          0%, 100% { opacity: 1; box-shadow: 0 0 0 0 currentColor; }
          50% { opacity: .7; box-shadow: 0 0 0 4px transparent; }
        }
        .hb-dot-pulse {
          animation: hb-dot-pulse 1.2s ease-in-out infinite;
        }
      `}</style>

      <div className="relative min-h-screen overflow-x-hidden bg-[#0d0d0f] font-hb text-[#d0d0d0] selection:bg-[#f6821f] selection:text-black">
        <div className="fixed inset-0 bg-grid-hb pointer-events-none" />
        <div
          className="h-1 fixed top-0 left-0 z-[60] hb-accent-bar"
          style={{ width: `${scrollPct * 100}%`, transition: "width 0.15s ease-out" }}
        />
        <MaintenanceBanner />
        {/* ─── 12-COL GRID (from #1) ───────────────────── */}
        <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 min-h-screen border-x border-[#1a1a1e] max-w-[1440px] mx-auto">
          {/* Left gutter */}
          <div className="hidden lg:block lg:col-span-1 border-r border-[#1a1a1e] relative">
            <div className="absolute bottom-20 left-1/2 -translate-x-1/2 -rotate-90 origin-center whitespace-nowrap text-[#969696] font-mono-hb text-xs uppercase tracking-widest">
              Bavimail / v1.0
            </div>
          </div>

          {/* Main column */}
          <div className="lg:col-span-10 flex flex-col">
            {/* ─── HEADER ── */}
            <header className="border-b border-[#1a1a1e] flex justify-between items-center px-6 md:px-8 py-4 bg-[#0d0d0f]/80 backdrop-blur-md sticky top-1 z-50">
              <div className="flex items-center gap-4">
                <BrandLogo className="h-auto w-[clamp(6rem,30vw,14.5rem)] sm:h-7 sm:w-auto" />
              </div>
              <div className="flex items-center gap-3">
                <ThemeToggle />
                <LoginCta variant="primary" size="md">
                  Start building
                </LoginCta>
              </div>
            </header>

            <main className="flex-grow flex flex-col items-center p-6 md:p-10 lg:p-14">
              <div className="w-full max-w-5xl">
                {/* ─── HERO (from #1 structure) ──────────── */}
                <section className="mb-6 md:mb-8">
                  <h1 className="font-hb font-extrabold text-4xl md:text-6xl lg:text-[4.25rem] leading-[1.05] tracking-tight mb-5">
                    One email API for
                    <br />
                    humans, apps, <span className="text-[#f6821f]">AI agents</span>.
                  </h1>
                  <p className="text-[#808080] text-lg md:text-xl max-w-2xl mb-8 leading-relaxed">
                    Transactional sends, marketing campaigns, inbound webhooks, and per-agent
                    inboxes. Drive it all from Claude, Cursor, or Cline through our MCP server. Free
                    with no credit card.
                  </p>

                  {/* Hero CTA */}
                  <div className="mb-10 flex flex-wrap items-center gap-4">
                    <LoginCta appearance="expanding-arrow">Start building free</LoginCta>
                  </div>

                  <div className="mb-10 flex flex-wrap gap-2.5">
                    <Link
                      to="/"
                      hash="features"
                      preload="intent"
                      className="inline-flex max-w-full"
                    >
                      <HeroTag>Inboxes for AI agents</HeroTag>
                    </Link>
                    <Link
                      to="/"
                      hash="features"
                      preload="intent"
                      className="inline-flex max-w-full"
                    >
                      <HeroTag>Transactional sends</HeroTag>
                    </Link>
                    <Link
                      to="/"
                      hash="features"
                      preload="intent"
                      className="inline-flex max-w-full"
                    >
                      <HeroTag>Inbound + webhooks</HeroTag>
                    </Link>
                    <Link to="/" hash="pricing" preload="intent" className="inline-flex max-w-full">
                      <HeroTag>View current plans and billing options</HeroTag>
                    </Link>
                  </div>

                  {/* SDK language logos */}
                  <div className="flex items-center gap-3 mb-10">
                    {/* TypeScript */}
                    <div className="w-10 h-10 rounded-full border border-dashed border-[#2a2a2e] flex items-center justify-center hover:border-[#3178c6]/50 transition-colors group/ts">
                      <svg
                        viewBox="0 0 256 256"
                        className="w-5 h-5 opacity-50 group-hover/ts:opacity-90 transition-opacity"
                      >
                        <rect width="256" height="256" rx="20" fill="#3178c6" />
                        <path
                          d="M150 200.5v27.6c4.5 2.3 9.8 4 15.9 5.2 6.1 1.2 12.6 1.7 19.4 1.7 6.6 0 12.9-.6 18.9-1.9 6-1.3 11.2-3.4 15.7-6.3 4.5-2.9 8-6.8 10.7-11.7 2.6-4.9 3.9-10.9 3.9-18 0-5.2-.9-9.8-2.6-13.7-1.7-3.9-4.1-7.4-7.2-10.5-3.1-3.1-6.8-5.9-11.1-8.5-4.3-2.5-9-5-14-7.4-3.6-1.7-6.9-3.4-9.9-5-2.9-1.6-5.5-3.2-7.5-4.8-2.1-1.6-3.7-3.3-4.8-5.2-1.1-1.8-1.7-4-1.7-6.4 0-2.2.5-4.1 1.5-5.8 1-1.7 2.5-3.2 4.3-4.4 1.8-1.2 4-2.1 6.4-2.8 2.5-.7 5.1-1 8-1 2.1 0 4.2.2 6.5.5 2.3.3 4.6.9 6.9 1.6 2.3.7 4.5 1.7 6.7 2.9 2.1 1.2 4 2.5 5.7 4.1V113.3c-3.9-1.6-8.1-2.9-12.7-3.7-4.6-.9-9.9-1.3-16-1.3-6.5 0-12.7.7-18.6 2.1-5.9 1.4-11 3.6-15.5 6.6-4.4 3-8 6.9-10.5 11.7-2.6 4.8-3.9 10.4-3.9 17.1 0 8.6 2.5 15.9 7.6 21.8 5.1 5.9 12.8 10.9 23.1 14.9 3.8 1.6 7.4 3.1 10.6 4.7 3.3 1.6 6.1 3.2 8.5 5 2.4 1.8 4.3 3.8 5.6 6 1.4 2.2 2.1 4.7 2.1 7.6 0 2.1-.5 3.9-1.4 5.6-.9 1.7-2.3 3.2-4.1 4.4-1.8 1.2-4 2.1-6.6 2.8-2.6.7-5.6 1-8.9 1-5.9 0-11.6-1.1-17.1-3.2-5.5-2.1-10.5-5.4-15-9.6zM106.6 120.5H60.5v-16.7h121.3v16.7h-46.1V242.2h-29V120.5z"
                          fill="white"
                        />
                      </svg>
                    </div>
                    <div className="w-3 h-px border-t border-dashed border-[#2a2a2e]" />
                    {/* Python */}
                    <div className="w-10 h-10 rounded-full border border-dashed border-[#2a2a2e] flex items-center justify-center hover:border-[#ffd43b]/50 transition-colors group/py">
                      <svg
                        viewBox="0 0 110 110"
                        className="w-5 h-5 opacity-50 group-hover/py:opacity-90 transition-opacity"
                      >
                        <path
                          d="M54.3 2C30 2 32 14.5 32 14.5V28h23v4H22S2 29 2 55s17.5 25 17.5 25h10V65s-.5-17.5 17-17.5h23.5S87 47.8 87 32V15S90 2 54.3 2zM40 10a4.5 4.5 0 110 9 4.5 4.5 0 010-9z"
                          fill="#3776ab"
                        />
                        <path
                          d="M55.7 108c24.3 0 22.3-12.5 22.3-12.5V82h-23v-4h33s20 3 20-23-17.5-25-17.5-25h-10v15s.5 17.5-17 17.5H40.5S23 62.2 23 78v17s-3 13 32.7 13zm14.3-8a4.5 4.5 0 110-9 4.5 4.5 0 010 9z"
                          fill="#ffd43b"
                        />
                      </svg>
                    </div>
                  </div>

                  {/* ── Dashboard Showcase Video ──────────────── */}
                  <div
                    className="relative -mx-20 sm:-mx-32 lg:-mx-56 xl:-mx-72 2xl:-mx-96"
                    style={{ marginTop: "-12vw" }}
                  >
                    <video
                      autoPlay
                      loop
                      muted
                      playsInline
                      poster="/dashboard-poster.png"
                      className="w-full"
                      style={{
                        background: "transparent",
                        maskImage:
                          "linear-gradient(to bottom, black 0%, black 50%, rgba(0,0,0,0.15) 75%, transparent 85%)",
                        WebkitMaskImage:
                          "linear-gradient(to bottom, black 0%, black 50%, rgba(0,0,0,0.15) 75%, transparent 85%)",
                      }}
                    >
                      <source src="/dashboard-loop.webm" type="video/webm" />
                      <source src="/dashboard-loop.mp4" type="video/mp4" />
                    </video>
                  </div>
                </section>

                {/* ─── FEATURES (stacked title from #3, grid with visuals from both) ── */}
                <section
                  className="relative z-10 -mt-36 md:-mt-52 lg:-mt-64 mb-20 md:mb-28"
                  id="features"
                >
                  <div className="crosshatch-hb border border-dashed border-[#1a1a1e] rounded-sm px-5 py-4 mb-8 inline-block">
                    <div className="text-[clamp(1.5rem,4vw,2.5rem)]">
                      <StackedTitle text="Features" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#1a1a1e] border border-[#1a1a1e]">
                    {features.map((feat) => (
                      <div
                        key={feat.num}
                        className="motion-hover-lift bg-[#0d0d0f] p-6 md:p-7 hover:bg-[#111114] transition-colors group h-full flex flex-col"
                      >
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-2">
                            <span className="font-mono-hb text-[11px] text-[#969696]">
                              /{feat.num}
                            </span>
                            <span className="text-[#f6821f] opacity-60 group-hover:opacity-100 transition-opacity">
                              {feat.icon}
                            </span>
                          </div>
                          <ArrowUpRight className="w-3.5 h-3.5 text-[#969696] group-hover:text-[#f6821f]/40 transition-colors" />
                        </div>
                        {/* Visual illustration from #3 */}
                        <div className="mb-4 p-4 border border-dashed border-[#1a1a1e] rounded-sm bg-[#0a0a0c]">
                          {feat.visual}
                        </div>
                        <h2 className="font-hb font-bold text-base text-white mb-1.5 group-hover:text-[#f6821f] transition-colors uppercase">
                          {feat.title}
                        </h2>
                        <p className="font-mono-hb text-[#8a8a8a] text-xs leading-relaxed">
                          {feat.desc}
                        </p>
                      </div>
                    ))}
                  </div>
                </section>

                {/* ─── MID-PAGE CTA ───────────────────────── */}
                <DashedBox className="bg-[#111114] mb-20 md:mb-28">
                  <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                    <div>
                      <h3 className="font-hb font-bold text-lg text-white mb-1">
                        Sending in under five minutes.
                      </h3>
                      <p className="font-mono-hb text-[12px] text-[#8a8a8a] max-w-md">
                        Install the TypeScript or Python SDK, verify a domain, and ship your first
                        send. Start free, no credit card.
                      </p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <LoginCta
                        variant="primary"
                        size="md"
                        className="group transition-[color,background-color,border-color,box-shadow] hover:shadow-[0_0_20px_rgba(246,130,31,0.25)]"
                      >
                        Start building
                        <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
                      </LoginCta>
                      <ButtonRouterLink
                        to="/"
                        hash="pricing"
                        preload="intent"
                        variant="outline"
                        size="md"
                      >
                        View pricing
                      </ButtonRouterLink>
                    </div>
                  </div>
                </DashedBox>

                {/* ─── BUILT FOR AI AGENTS ────────────────── */}
                <section className="mb-20 md:mb-28">
                  <div className="crosshatch-hb border border-dashed border-[#1a1a1e] rounded-sm px-5 py-4 mb-8 inline-block">
                    <div className="text-[clamp(1.5rem,4vw,2.5rem)]">
                      <StackedTitle text="AI Agents" />
                    </div>
                  </div>
                  <h2 className="font-hb font-extrabold text-3xl md:text-4xl leading-tight tracking-tight mb-2">
                    A real inbox for <span className="text-[#f6821f]">every agent</span> you ship
                  </h2>
                  <p className="text-[#929292] text-base mb-8 max-w-2xl">
                    Stand up a fleet of AI agents that handle support tickets and customer replies
                    over real two-way email. Each agent gets its own sending alias on your domain,
                    with SPF, DKIM, DMARC, and MAIL FROM managed for you. No shared mailboxes, no
                    OAuth.
                  </p>
                  <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
                    <DashedBox>
                      <div className="flex items-center gap-2 mb-3">
                        <Send className="w-4 h-4 text-[#f6821f]" />
                        <span className="font-hb font-bold text-sm text-white">
                          A sending alias per agent
                        </span>
                      </div>
                      <p className="text-[#929292] text-[13px] leading-relaxed">
                        Spin up support@, billing@, agent-42@ on your own domain. One API call gives
                        every agent its own identity, no shared inbox required.
                      </p>
                    </DashedBox>
                    <DashedBox>
                      <div className="flex items-center gap-2 mb-3">
                        <Webhook className="w-4 h-4 text-[#f6821f]" />
                        <span className="font-hb font-bold text-sm text-white">
                          Replies straight into your app
                        </span>
                      </div>
                      <p className="text-[#929292] text-[13px] leading-relaxed">
                        Inbound email arrives as a signed webhook with parsed body, attachments, and
                        threading. Your agent routes it the same way it handles any other event.
                      </p>
                    </DashedBox>
                    <DashedBox>
                      <div className="flex items-center gap-2 mb-3">
                        <ShieldCheck className="w-4 h-4 text-[#f6821f]" />
                        <span className="font-hb font-bold text-sm text-white">
                          Reputation we manage for you
                        </span>
                      </div>
                      <p className="text-[#929292] text-[13px] leading-relaxed">
                        SPF, DKIM, DMARC, and MAIL FROM are configured automatically. Bounces,
                        complaints, and suppression sync run in the background while you build.
                      </p>
                    </DashedBox>
                    <DashedBox>
                      <div className="flex items-center gap-2 mb-3">
                        <ArrowUpRight className="w-4 h-4 text-[#f6821f]" />
                        <span className="font-hb font-bold text-sm text-white">
                          Drop into any framework
                        </span>
                      </div>
                      <p className="text-[#929292] text-[13px] leading-relaxed">
                        Plain REST with TypeScript and Python SDKs. Works with LangChain, CrewAI,
                        n8n, or anything that can make an HTTP request.
                      </p>
                    </DashedBox>
                  </div>
                </section>

                {/* ─── CODE + DEPLOY (from #1, with #3 styling) ─── */}
                <section className="mb-20 md:mb-28">
                  <h2 className="font-hb font-extrabold text-3xl md:text-4xl leading-tight tracking-tight mb-2">
                    From first line to <span className="text-[#f6821f]">full scale</span>
                  </h2>
                  <p className="text-[#929292] text-base mb-8 max-w-lg">
                    Deploy working email infrastructure in seconds or start from our SDK.
                  </p>
                  <div className="grid md:grid-cols-2 gap-5">
                    <DashedBox noPad className="bg-[#111114] overflow-hidden h-full">
                      <div className="flex border-b border-dashed border-[#2a2a2e]">
                        <div className="px-4 py-2 font-mono-hb text-xs text-[#f6821f] bg-[#1a1a1e] border-r border-dashed border-[#2a2a2e]">
                          send.ts
                        </div>
                        <div className="px-4 py-2 font-mono-hb text-xs text-[#8a8a8a] border-r border-dashed border-[#2a2a2e] select-none">
                          webhook.ts
                        </div>
                        <div className="px-4 py-2 font-mono-hb text-xs text-[#8a8a8a] select-none">
                          config.json
                        </div>
                      </div>
                      <div className="p-5 font-mono-hb text-[13px] text-[#888] leading-7 overflow-x-auto">
                        <span className="text-[#8a8a8a]">import</span> {"{ Bavimail }"}{" "}
                        <span className="text-[#8a8a8a]">from</span>{" "}
                        <span className="text-[#f6821f]">"bavimail"</span>;<br />
                        <br />
                        <span className="text-[#8a8a8a]">const</span> mail ={" "}
                        <span className="text-[#8a8a8a]">new</span>{" "}
                        <span className="text-white">Bavimail</span>({"{"}
                        <br />
                        &nbsp;&nbsp;apiKey: process.env.
                        <span className="text-white">BAVIMAIL_API_KEY</span>,<br />
                        {"}"});
                        <br />
                        <br />
                        <span className="text-[#8a8a8a]">await</span> mail.
                        <span className="text-white">emails.send</span>({"{"}
                        <br />
                        &nbsp;&nbsp;aliasId: <span className="text-[#f6821f]">"your-alias-id"</span>
                        ,<br />
                        &nbsp;&nbsp;toEmail:{" "}
                        <span className="text-[#f6821f]">"hello@example.com"</span>,<br />
                        &nbsp;&nbsp;subject:{" "}
                        <span className="text-[#f6821f]">"Welcome aboard"</span>,<br />
                        &nbsp;&nbsp;body:{" "}
                        <span className="text-[#f6821f]">
                          "&lt;h1&gt;Hello from Bavimail&lt;/h1&gt;"
                        </span>
                        ,<br />
                        {"}"});
                      </div>
                    </DashedBox>
                    <DashedBox noPad className="bg-[#111114] overflow-hidden h-full">
                      {/* Terminal chrome */}
                      <div className="flex items-center gap-2 px-4 py-2 border-b border-dashed border-[#2a2a2e]">
                        <span className="w-2.5 h-2.5 rounded-full bg-[#2a2a2e]" />
                        <span className="w-2.5 h-2.5 rounded-full bg-[#2a2a2e]" />
                        <span className="w-2.5 h-2.5 rounded-full bg-[#2a2a2e]" />
                        <span className="ml-2 font-mono-hb text-[10px] text-[#969696]">
                          ~/project &mdash; node
                        </span>
                      </div>
                      {/* Terminal output */}
                      <div className="p-5 font-mono-hb text-[13px] leading-7">
                        <div className="text-[#969696]">$ npm run dev</div>
                        <div className="mt-2 text-[#969696]">
                          <span className="text-[#8a8a8a]">bavimail-app</span> v1.0.0
                        </div>
                        <div className="text-[#969696]">
                          &rarr; Local:{" "}
                          <span className="text-[#929292]">http://localhost:3000</span>
                        </div>
                        <div className="mt-3 flex items-center gap-2 text-[#10b981]">
                          <Check className="w-3.5 h-3.5 shrink-0" /> Server ready
                        </div>
                        <div className="mt-4 border-t border-dashed border-[#1a1a1e] pt-4 space-y-2">
                          <div className="text-[#969696]">
                            <span className="text-[#f6821f]">POST</span>{" "}
                            <span className="text-[#929292]">/api/send</span>{" "}
                            <span className="text-[#10b981]">200</span>{" "}
                            <span className="text-[#969696]">42ms</span>
                          </div>
                          <div className="text-[#969696]">
                            Sending to <span className="text-[#929292]">alice@example.com</span>
                            <span className="text-[#969696]"> ... </span>
                            <span className="text-[#10b981]">delivered</span>
                          </div>
                          <div className="text-[#969696]">
                            Sending to <span className="text-[#929292]">bob@company.io</span>
                            <span className="text-[#969696]"> ... </span>
                            <span className="text-[#10b981]">delivered</span>
                          </div>
                          <div className="text-[#969696]">
                            <span className="text-[#f6821f]">POST</span>{" "}
                            <span className="text-[#929292]">/api/send</span>{" "}
                            <span className="text-[#10b981]">200</span>{" "}
                            <span className="text-[#969696]">38ms</span>
                          </div>
                          <div className="text-[#969696]">
                            Sending to <span className="text-[#929292]">team@startup.dev</span>
                            <span className="text-[#969696]"> ... </span>
                            <span className="text-[#10b981]">delivered</span>
                          </div>
                        </div>
                        <div className="mt-3 text-[#969696] text-[11px]">3 emails sent</div>
                      </div>
                    </DashedBox>
                  </div>
                </section>

                {/* ─── EXAMPLES (from #3: stacked title + sidebar + code) ── */}
                <section className="mb-20 md:mb-28" id="examples">
                  <div className="crosshatch-hb border border-dashed border-[#1a1a1e] rounded-sm px-5 py-4 mb-8 inline-block">
                    <div className="text-[clamp(1.5rem,4vw,2.5rem)]">
                      <StackedTitle text="Examples" />
                    </div>
                  </div>

                  <DashedBox noPad className="bg-[#111114] overflow-hidden">
                    <Tabs
                      variant="segment"
                      orientation="vertical"
                      value={activeExample}
                      onValueChange={setActiveExample}
                      className="grid grid-cols-1 md:grid-cols-[240px_1fr] divide-y md:divide-y-0 md:divide-x divide-dashed divide-[#2a2a2e]"
                    >
                      {/* Sidebar */}
                      <div className="flex flex-col">
                        <TabsList aria-label="Code examples">
                          {examples.map((ex) => (
                            <TabsTrigger key={ex.label} value={ex.label}>
                              {ex.label}
                              <ChevronRight className="h-4 w-4 shrink-0" />
                            </TabsTrigger>
                          ))}
                        </TabsList>
                        <div className="hidden md:block flex-1 border-t border-dashed border-[#2a2a2e] crosshatch-dense-hb min-h-[60px]" />
                      </div>
                      {/* Code viewer */}
                      <div className="relative bg-[#0c0c0e] min-h-[360px] overflow-hidden">
                        <CopyButton
                          key={activeExample}
                          iconOnly
                          variant="ghost"
                          size="icon"
                          value={() =>
                            examples.find((ex) => ex.label === activeExample)?.code ?? ""
                          }
                          className="absolute top-3 right-3 z-10"
                        />
                        {examples.map((ex) => (
                          <TabsContent key={ex.label} value={ex.label}>
                            <pre className="p-5 md:p-6 font-mono-hb text-[13px] leading-7 overflow-x-auto whitespace-pre-wrap">
                              <code>
                                {ex.code.split("\n").map((line, idx) => {
                                  // Escape first: sample code contains markup such as <h1>,
                                  // which would otherwise be injected as real elements.
                                  const h = line
                                    .replace(/&/g, "&amp;")
                                    .replace(/</g, "&lt;")
                                    .replace(/>/g, "&gt;")
                                    .replace(
                                      /(import|from|const|new|await|switch|case|break|console|return|forEach|export|default|async|function)/g,
                                      "<kw>$1</kw>",
                                    )
                                    .replace(/(".*?")/g, "<str>$1</str>")
                                    .replace(/(\/\/.*)/g, "<cm>$1</cm>");
                                  return (
                                    <span key={idx}>
                                      <span
                                        dangerouslySetInnerHTML={{
                                          __html: h
                                            .replace(/<kw>/g, '<span style="color:#8a8a8a">')
                                            .replace(/<\/kw>/g, "</span>")
                                            .replace(/<str>/g, '<span style="color:#f6821f">')
                                            .replace(/<\/str>/g, "</span>")
                                            .replace(/<cm>/g, '<span style="color:#6b6b6b">')
                                            .replace(/<\/cm>/g, "</span>"),
                                        }}
                                      />
                                      {"\n"}
                                    </span>
                                  );
                                })}
                              </code>
                            </pre>
                          </TabsContent>
                        ))}
                      </div>
                    </Tabs>
                  </DashedBox>
                </section>

                {/* ─── PRICING ─────────────────────────────── */}
                <section className="mb-20 md:mb-28" id="pricing">
                  <h2 className="font-hb font-extrabold text-3xl md:text-4xl mb-4">
                    Pricing that grows with you
                  </h2>
                  <p className="mb-8 text-hb-muted">
                    Start free. Compare current plans and choose the capacity your product needs.
                  </p>
                  <PricingPlans compact />
                  <Link
                    to="/pricing"
                    preload="intent"
                    className="mt-6 inline-block text-hb-accent underline"
                  >
                    All plans, limits, and pricing questions
                  </Link>
                </section>

                {/* ─── FAQ ──────────────────────────────── */}
                <section className="mb-20 md:mb-28" id="faq">
                  <h2 className="font-hb font-extrabold text-3xl md:text-4xl leading-tight tracking-tight mb-2">
                    Frequently asked <span className="text-[#f6821f]">questions</span>
                  </h2>
                  <p className="text-[#929292] text-base mb-10">
                    Have other questions? Get in touch at{" "}
                    <a
                      href="mailto:support@bavimail.com"
                      className="text-[#f6821f] underline transition-colors"
                    >
                      support@bavimail.com
                    </a>
                  </p>
                  <div className="grid md:grid-cols-2 gap-x-8 lg:gap-x-14">
                    <BouncyAccordion
                      indicator="plus"
                      items={faqAccordionItems.slice(0, 3)}
                      value={openFaq}
                      onValueChange={setOpenFaq}
                    />
                    <BouncyAccordion
                      indicator="plus"
                      items={faqAccordionItems.slice(3)}
                      value={openFaq}
                      onValueChange={setOpenFaq}
                      classNames={{ root: "max-md:border-t-0" }}
                    />
                  </div>
                </section>

                {/* ─── PERFORMANCE (from #1) ────────────── */}
                <section className="mb-20 md:mb-28">
                  <h2 className="font-hb font-extrabold text-3xl uppercase mb-6">
                    Raw Performance.
                  </h2>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[
                      { value: 13, suffix: undefined, label: "Webhook Events" },
                      { value: 2, suffix: undefined, label: "Official SDKs" },
                      { value: 10, suffix: "K+", label: "Emails Handled" },
                      { value: 20, suffix: "K+", label: "Events Processed" },
                    ].map((stat) => (
                      <DashedBox key={stat.label} className="bg-[#111114] text-center">
                        <div className="font-mono-hb text-2xl text-[#f6821f] mb-1">
                          <NumberTicker value={stat.value} suffix={stat.suffix} />
                        </div>
                        <div className="font-hb text-[#8a8a8a] uppercase text-[10px] font-bold tracking-wider">
                          {stat.label}
                        </div>
                      </DashedBox>
                    ))}
                  </div>
                </section>

                {/* ─── BOTTOM CTA ──────────────────────────── */}
                <section className="mb-16">
                  <div className="relative border border-dashed border-[#2a2a2e] overflow-hidden">
                    {/* Corner dots */}
                    <div className="absolute -top-[3px] -left-[3px] w-[6px] h-[6px] border border-[#444] bg-[#0d0d0f] z-10" />
                    <div className="absolute -top-[3px] -right-[3px] w-[6px] h-[6px] border border-[#444] bg-[#0d0d0f] z-10" />
                    <div className="absolute -bottom-[3px] -left-[3px] w-[6px] h-[6px] border border-[#444] bg-[#0d0d0f] z-10" />
                    <div className="absolute -bottom-[3px] -right-[3px] w-[6px] h-[6px] border border-[#444] bg-[#0d0d0f] z-10" />

                    {/* Gradient accent line at top */}
                    <div className="h-px w-full bg-gradient-to-r from-transparent via-[#f6821f]/40 to-transparent" />

                    <div className="bg-[#111114] crosshatch-hb px-6 md:px-10 py-12 md:py-16 text-center">
                      <div className="max-w-2xl mx-auto">
                        <h2 className="font-hb font-extrabold text-3xl md:text-4xl lg:text-5xl leading-tight tracking-tight text-white mb-4">
                          One API for every email{" "}
                          <span className="text-[#f6821f]">your app sends or receives</span>
                        </h2>
                        <p className="text-[#929292] text-base md:text-lg mb-8 max-w-lg mx-auto leading-relaxed">
                          Password resets, newsletters, customer replies, AI-agent inboxes. Same
                          SDK, same dashboard, same bill.
                        </p>
                        <div className="mb-8 flex flex-wrap items-center justify-center gap-4">
                          <LoginCta
                            variant="primary"
                            size="lg"
                            className="group transition-[color,background-color,border-color,box-shadow] hover:shadow-[0_0_30px_rgba(246,130,31,0.3)]"
                          >
                            Start building free
                            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                          </LoginCta>
                          <ButtonLink
                            href="mailto:support@bavimail.com"
                            variant="outline"
                            size="lg"
                            className="group"
                          >
                            Talk to sales
                            <ArrowUpRight className="h-4 w-4 opacity-40 transition-opacity group-hover:opacity-100" />
                          </ButtonLink>
                        </div>
                        <div className="flex flex-wrap items-center justify-center gap-6 font-mono-hb text-[11px] text-[#969696]">
                          <span className="flex items-center gap-1.5">
                            <Check className="w-3 h-3 text-[#f6821f]/60" /> Free tier included
                          </span>
                          <span className="flex items-center gap-1.5">
                            <Check className="w-3 h-3 text-[#f6821f]/60" /> No credit card required
                          </span>
                          <span className="flex items-center gap-1.5">
                            <Check className="w-3 h-3 text-[#f6821f]/60" /> Setup in minutes
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              </div>
            </main>

            {/* ─── FOOTER ──────────────────────────────── */}
            <footer className="border-t border-[#1a1a1e] bg-[#0a0a0c] py-8 px-6 md:px-10">
              <div className="max-w-5xl mx-auto">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mb-10">
                  <div className="col-span-2 md:col-span-1">
                    <BrandLogo className="h-auto w-56 max-w-full mb-3" />
                    <p className="font-mono-hb text-[10px] text-[#969696] leading-relaxed">
                      Email for your apps. Inboxes for your AI agents.
                    </p>
                  </div>
                  {[
                    {
                      t: "Product",
                      l: [
                        { label: "Features", href: "#features" },
                        { label: "Pricing", href: "#pricing" },
                        { label: "Compare", href: "/compare" },
                      ],
                    },
                    {
                      t: "Developers",
                      l: [
                        { label: "SDKs", href: "https://github.com/orgs/Bavlio/repositories" },
                        { label: "Documentation", href: "/docs" },
                        { label: "Status", href: "https://status.bavimail.com" },
                      ],
                    },
                    {
                      t: "Company",
                      l: [{ label: "Contact", href: "mailto:support@bavimail.com" }],
                    },
                    {
                      t: "Legal",
                      l: [
                        { label: "Privacy", href: "/privacy" },
                        { label: "Terms", href: "/terms" },
                      ],
                    },
                  ].map((col) => (
                    <div key={col.t}>
                      <h3 className="font-mono-hb text-[10px] uppercase tracking-widest text-[#969696] mb-3">
                        {col.t}
                      </h3>
                      <ul className="space-y-2">
                        {col.l.map((link) => (
                          <li key={link.label}>
                            <a
                              href={link.href}
                              className="font-mono-hb text-xs text-[#969696] hover:text-[#f6821f] transition-colors"
                            >
                              {link.label}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
                <div className="border-t border-[#1a1a1e] pt-6 flex flex-col md:flex-row justify-between items-center gap-4">
                  <div className="flex flex-col gap-1">
                    <span className="font-mono-hb text-xs text-[#969696]">
                      &copy; {new Date().getFullYear()} Bavimail. All rights reserved.
                    </span>
                    <BrandFamilyLine
                      className="font-mono-hb text-xs text-[#969696]"
                      linkClassName="underline underline-offset-2 hover:text-[#f6821f]"
                    />
                  </div>
                  <div className="flex gap-5">
                    <a
                      href="https://x.com/sam_cannas"
                      className="font-mono-hb text-xs text-[#969696] hover:text-[#f6821f] transition-colors"
                    >
                      Twitter
                    </a>
                    <a
                      href="https://github.com/orgs/Bavlio/repositories"
                      className="font-mono-hb text-xs text-[#969696] hover:text-[#f6821f] transition-colors"
                    >
                      GitHub
                    </a>
                    {/* TODO: finish discord */}
                    {/* <a href="https://discord.gg/fWXN72cGsQ" className="font-mono-hb text-xs text-[#969696] hover:text-[#f6821f] transition-colors">Discord</a> */}
                    <a
                      href="https://status.bavimail.com"
                      className="font-mono-hb text-xs text-[#969696] hover:text-[#f6821f] transition-colors"
                    >
                      Status
                    </a>
                    <a
                      href="mailto:support@bavimail.com"
                      className="font-mono-hb text-xs text-[#969696] hover:text-[#f6821f] transition-colors"
                    >
                      Email
                    </a>
                  </div>
                </div>
              </div>
            </footer>
          </div>

          {/* Right gutter */}
          <div className="hidden lg:block lg:col-span-1 border-l border-[#1a1a1e]" />
        </div>
      </div>
    </>
  );
}
